/**
 * Offisielle signalflagg som SVG.
 *
 * - Blue Peter (P): blå bakgrunn med hvit firkant i midten
 * - Klasseflagg G: gule og blå vertikale striper
 * - AP (Answering Pennant): røde og hvite vertikale striper, svalehale-form
 */

interface FlagProps {
  up: boolean;
  label: string;
  children: React.ReactNode;
}

export function FlagFrame({ up, label, children }: FlagProps) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`
          transition-all duration-500 ease-out
          ${up ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-40 scale-90'}
        `}
      >
        {children}
      </div>
      <div className={`text-xs font-semibold uppercase tracking-wide ${up ? 'text-yellow-300' : 'text-slate-500'}`}>
        {label}
      </div>
    </div>
  );
}

export function BluePeterFlag({ up }: { up: boolean }) {
  return (
    <FlagFrame up={up} label="Blue Peter (P)">
      <svg viewBox="0 0 90 60" className="w-20 h-14 md:w-28 md:h-20 drop-shadow-md" role="img" aria-label="Blue Peter">
        <rect width="90" height="60" fill="#0d4da8" />
        <rect x="30" y="18" width="30" height="24" fill="#ffffff" />
        <rect x="0" y="0" width="90" height="60" fill="none" stroke="#0a2f66" strokeWidth="2" />
      </svg>
    </FlagFrame>
  );
}

export function ClassFlagG({ up }: { up: boolean }) {
  // International Code Flag "G" — gule og blå vertikale striper (6 striper)
  const stripes = [
    '#facc15', // gul
    '#1d4ed8', // blå
    '#facc15',
    '#1d4ed8',
    '#facc15',
    '#1d4ed8',
  ];
  return (
    <FlagFrame up={up} label="Klasseflagg G">
      <svg viewBox="0 0 90 60" className="w-20 h-14 md:w-28 md:h-20 drop-shadow-md" role="img" aria-label="Klasseflagg G">
        {stripes.map((color, i) => (
          <rect key={i} x={i * 15} y="0" width="15" height="60" fill={color} />
        ))}
        <rect x="0" y="0" width="90" height="60" fill="none" stroke="#111" strokeWidth="1.5" />
      </svg>
    </FlagFrame>
  );
}

export function APFlag({ up }: { up: boolean }) {
  // Answering Pennant — røde og hvite vertikale striper med svalehale
  return (
    <FlagFrame up={up} label="AP — Utsatt">
      <svg viewBox="0 0 100 60" className="w-24 h-14 md:w-32 md:h-20 drop-shadow-md" role="img" aria-label="AP-flagg">
        <defs>
          <clipPath id="pennant-clip">
            {/* Svalehale-pennant-form: firkant med kile kuttet ut på høyre */}
            <polygon points="0,0 80,0 100,30 80,60 0,60" />
          </clipPath>
        </defs>
        <g clipPath="url(#pennant-clip)">
          <rect x="0" y="0" width="16" height="60" fill="#ffffff" />
          <rect x="16" y="0" width="16" height="60" fill="#dc2626" />
          <rect x="32" y="0" width="16" height="60" fill="#ffffff" />
          <rect x="48" y="0" width="16" height="60" fill="#dc2626" />
          <rect x="64" y="0" width="36" height="60" fill="#ffffff" />
        </g>
      </svg>
    </FlagFrame>
  );
}
